// hooks/useMenu.ts

"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import { fetchMenuService } from "@/lib/services/menu.service"

import type { Menu } from "@/types/models/menu"

interface UseMenuParams {
  tenantId?: number | null
  enabled?: boolean
}

interface UseMenuReturn {
  menu: Menu | null
  isLoading: boolean
  error: unknown
  refetch: () => Promise<void>
}

/**
 * =========================================================
 * USE MENU HOOK
 * =========================================================
 * Handles:
 * - fetching menu
 * - loading state
 * - error state
 * - refetching
 */

export function useMenu({
  tenantId,
  enabled = true,
}: UseMenuParams): UseMenuReturn {
  const [menu, setMenu] = useState<Menu | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const refetch = useCallback(async () => {
    if (!enabled || !tenantId) {
      setMenu(null)
      setError(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const data = await fetchMenuService(tenantId)

      setMenu(data)
    } catch (err) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }, [enabled, tenantId])

  useEffect(() => {
    if (!enabled || !tenantId) {
      setMenu(null)
      setError(null)
      setIsLoading(false)
      return
    }

    void refetch()
  }, [tenantId, refetch])

  return useMemo(
    () => ({
      menu,
      isLoading,
      error,
      refetch,
    }),
    [menu, isLoading, error, refetch]
  )
}
